import React from 'react';
import { Box, Heading, Text, Button, VStack, useColorModeValue } from '@chakra-ui/react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          resetError={this.handleReset}
          componentName={this.props.componentName}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ error, resetError, componentName }) => {
  const bgColor = useColorModeValue('background.shade1');
  const boxBgColor = useColorModeValue('background.shade2');

  return (
    <Box bg={bgColor} minHeight="100vh" display="flex" alignItems="center" justifyContent="center" p={4}>
      <Box bg={boxBgColor} p={8} borderRadius="md" border="1px solid" borderColor="border.default" maxWidth="600px">
        <VStack spacing={4} align="stretch">
          <Heading as="h2" size="lg" color="red.500">
            Something went wrong
          </Heading>

          <Text>
            {componentName ? `The ${componentName} component` : 'A component'} encountered an error and couldn't render properly.
          </Text>

          {error && (
            <Box p={4} bg="red.50" borderRadius="md" border="1px solid" borderColor="red.200">
              <Text fontSize="sm" fontFamily="monospace" color="red.700">
                {error.toString()}
              </Text>
            </Box>
          )}

          <VStack spacing={2}>
            <Button onClick={resetError} colorScheme="teal" width="full">
              Try Again
            </Button>
            <Button onClick={() => window.location.href = '/'} variant="outline" width="full">
              Go to Home
            </Button>
          </VStack>

          <Text fontSize="sm" color="gray.600">
            If this problem persists, please refresh the page or contact support.
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default ErrorBoundary;
